import type {OttopilotDispositionConfig,
OttopilotDispositionOptionRule,
OttopilotSuggestedJourneyStatusRule} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerDispositionConfig";
import {OttopilotDispositionOptionRuleActionKind,
OttopilotDispositionOptionRuleCountMode,
OttopilotDispositionPluginKind,
OttopilotSuggestedJourneyStatusRuleKind} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerDispositionConfig";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {DateTime} from "luxon";
import {flatDispositionToDisplayNameFormat} from "~/utilities/utilities";

export function countDispositionOccurrences(
    dispositionIds: Array<Uuid>,
    targetDispositionId: Uuid,
    countMode: OttopilotDispositionOptionRuleCountMode,
): number {
    if (countMode === OttopilotDispositionOptionRuleCountMode.totalInHistory) {
        return dispositionIds.filter((id) => id === targetDispositionId).length;
    }

    let count = 0;
    for (const dispositionId of dispositionIds) {
        if (dispositionId !== targetDispositionId) {
            break;
        }
        count += 1;
    }
    return count;
}

function applyLabelTemplate(
    labelTemplate: string,
    segment: string,
    count: number,
): string {
    return labelTemplate
        .replaceAll("{segment}", flatDispositionToDisplayNameFormat(segment))
        .replaceAll("{count}", String(count))
        .replaceAll("{countNext}", String(count + 1));
}

function ruleMatchesPath(
    rule: OttopilotDispositionOptionRule,
    level: number,
    selected: Array<string>,
): boolean {
    if (rule.when.level !== level) {
        return false;
    }

    const prefix = selected.slice(0, rule.when.selectedPrefix.length);
    return prefix.join("|") === rule.when.selectedPrefix.join("|");
}

function dispositionIdsForNonAllowedCheck(args: {
    previousDispositionIds: Array<Uuid>;
    currentDispositionId: Uuid | null | undefined;
}): Array<Uuid> {
    if (args.currentDispositionId === null || args.currentDispositionId === undefined) {
        return args.previousDispositionIds;
    }

    return [...args.previousDispositionIds, args.currentDispositionId];
}

function optionMatchesTargetSegment(
    option: {value: string; label: string},
    targetSegment: string,
): boolean {
    if (option.value === targetSegment) {
        return true;
    }

    const targetLabel = flatDispositionToDisplayNameFormat(targetSegment);
    if (option.label === targetLabel) {
        return true;
    }

    if (flatDispositionToDisplayNameFormat(option.value) === targetLabel) {
        return true;
    }

    return false;
}

function filterOutTargetSegment(
    options: Array<{value: string; label: string}>,
    targetSegment: string,
): Array<{value: string; label: string}> {
    return options.filter((item) => optionMatchesTargetSegment(item, targetSegment) === false);
}

export function applyDispositionOptionRules(args: {
    options: Array<{value: string; label: string}>;
    level: number;
    selected: Array<string>;
    previousDispositionIds: Array<Uuid>;
    currentDispositionId?: Uuid | null;
    dispositionConfig: OttopilotDispositionConfig;
}): Array<{value: string; label: string}> {
    const optionRulesPlugin = args.dispositionConfig.plugins.find(
        (plugin) => plugin.kind === OttopilotDispositionPluginKind.optionRules,
    );
    if (optionRulesPlugin === undefined) {
        return args.options;
    }

    const hasHistoryPlugin = args.dispositionConfig.plugins.some(
        (plugin) => plugin.kind === OttopilotDispositionPluginKind.dispositionHistory,
    );
    if (hasHistoryPlugin === false) {
        return args.options;
    }

    let transformedOptions = [...args.options];
    for (const rule of optionRulesPlugin.rules) {
        if (ruleMatchesPath(rule, args.level, args.selected) === false) {
            continue;
        }

        const count = countDispositionOccurrences(
            args.previousDispositionIds,
            rule.countDispositionId,
            rule.countMode,
        );

        const action = rule.action;

        if (action.kind === OttopilotDispositionOptionRuleActionKind.hideWhenHistoryContainsNonAllowedDispositionId) {
            const dispositionIdsToCheck = dispositionIdsForNonAllowedCheck({
                previousDispositionIds: args.previousDispositionIds,
                currentDispositionId: args.currentDispositionId,
            });
            const hasNonAllowed = dispositionIdsToCheck.some(
                (id) => action.allowedDispositionIds.includes(id) === false,
            );
            if (hasNonAllowed === true) {
                transformedOptions = filterOutTargetSegment(transformedOptions, rule.targetSegment);
            }
            continue;
        }

        if (action.kind === OttopilotDispositionOptionRuleActionKind.hideWhenCurrentDispositionIdEquals) {
            if (
                args.currentDispositionId !== null &&
                args.currentDispositionId !== undefined &&
                args.currentDispositionId === rule.countDispositionId
            ) {
                transformedOptions = filterOutTargetSegment(transformedOptions, rule.targetSegment);
            }
            continue;
        }

        if (action.kind === OttopilotDispositionOptionRuleActionKind.hideWhenCountGte) {
            if (count >= action.max) {
                transformedOptions = filterOutTargetSegment(transformedOptions, rule.targetSegment);
            }
            continue;
        }

        if (action.kind === OttopilotDispositionOptionRuleActionKind.setLabelWhenCountLt && count < action.max) {
            transformedOptions = transformedOptions.map((item) => {
                if (optionMatchesTargetSegment(item, rule.targetSegment) === false) {
                    return item;
                }

                return {
                    value: item.value,
                    label: applyLabelTemplate(action.labelTemplate, item.value, count),
                };
            });
        }
    }

    return transformedOptions;
}

function evaluateSuggestedRule(args: {
    rule: OttopilotSuggestedJourneyStatusRule;
    selectedDispositionId: Uuid;
    previousDispositionIds: Array<Uuid>;
    journeyCreatedAtIso?: string | null;
}): Uuid | null {
    if (args.rule.kind === OttopilotSuggestedJourneyStatusRuleKind.dispositionDateWithin) {
        if (args.selectedDispositionId !== args.rule.triggerDispositionId) {
            return null;
        }
        if (args.journeyCreatedAtIso === undefined || args.journeyCreatedAtIso === null) {
            return null;
        }

        const IST_ZONE = "Asia/Kolkata";
        const nowIst = DateTime.now().setZone(IST_ZONE);
        const createdAtIst = DateTime.fromISO(args.journeyCreatedAtIso).setZone(IST_ZONE);
        if (createdAtIst.isValid !== true) {
            return null;
        }

        if (createdAtIst > nowIst.minus({days: args.rule.timeFrom})) {
            return args.rule.suggested;
        }

        return null;
    }

    if (args.rule.kind === OttopilotSuggestedJourneyStatusRuleKind.dispositionIdInSet) {
        return args.rule.dispositionIds.includes(args.selectedDispositionId) ? args.rule.suggested : null;
    }

    const isMatchingDisposition = args.selectedDispositionId === args.rule.triggerDispositionId;
    if (isMatchingDisposition === false) {
        return null;
    }

    const consecutiveCount = countDispositionOccurrences(
        args.previousDispositionIds,
        args.rule.countDispositionId,
        OttopilotDispositionOptionRuleCountMode.consecutiveFromNewest,
    );
    if (consecutiveCount >= args.rule.consecutiveMin) {
        return args.rule.suggested;
    }

    return null;
}

export function evaluateSuggestedJourneyStatus(args: {
    dispositionConfig: OttopilotDispositionConfig;
    selectedDispositionId: Uuid | null;
    previousDispositionIds: Array<Uuid>;
    journeyCreatedAtIso?: string | null;
}): Uuid | null {
    if (args.selectedDispositionId === null) {
        return null;
    }

    const suggestedRulesPlugin = args.dispositionConfig.plugins.find(
        (plugin) => plugin.kind === OttopilotDispositionPluginKind.suggestedJourneyStatusRules,
    );
    if (suggestedRulesPlugin === undefined) {
        return null;
    }

    for (const rule of suggestedRulesPlugin.rules) {
        const outcome = evaluateSuggestedRule({
            rule: rule,
            selectedDispositionId: args.selectedDispositionId,
            previousDispositionIds: args.previousDispositionIds,
            journeyCreatedAtIso: args.journeyCreatedAtIso,
        });
        if (outcome !== null) {
            return outcome;
        }
    }

    return null;
}
